package com.devi3east.bashbuddy.cart.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import com.devi3east.bashbuddy.account.profile.domain.models.Profile
import com.devi3east.bashbuddy.cart.results.CheckoutResult
import com.devi3east.bashbuddy.cart.ui.components.CartOrganizationCard
import com.devi3east.bashbuddy.cart.ui.components.EmptyCartContent
import com.devi3east.bashbuddy.cart.ui.components.InventoryAlertDialog
import com.devi3east.bashbuddy.dashboard.ui.components.dialogs.InfoAlertDialog
import com.devi3east.bashbuddy.features.app.data.AppState
import com.devi3east.bashbuddy.features.app.data.Screen
import com.devi3east.bashbuddy.features.detail.ui.LoadingScreen
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.launch

@Composable
fun CartScreenRoute(
    appState: AppState
) {
    val authState by appState.authViewModel.uiState.collectAsState()
    val profile = authState.profile

    if (profile == null) {
        LoadingScreen()
        return
    }

    val uiState by appState.cartViewModel.uiState.collectAsState()
    val snackbarHostState = remember { SnackbarHostState() }

    LaunchedEffect(Unit) {
        appState.cartViewModel.uiEvent.collectLatest { event ->
            when (event) {
                is CartUiEvent.ShowMessage -> snackbarHostState.showSnackbar(event.message)
                CartUiEvent.CheckoutSuccess -> { snackbarHostState.showSnackbar("Redirecting to checkout...") }
                CartUiEvent.CheckoutCancelled -> snackbarHostState.showSnackbar("Checkout cancelled")
                is CartUiEvent.InventoryError -> snackbarHostState.showSnackbar(event.message)
            }
        }
    }

    CartScreen(
        appState = appState,
        profile = profile,
        uiState = uiState,
        onUpdateQuantity = { ticketId, newQty ->
            appState.cartViewModel.updateQuantity(ticketId, newQty)
        },
        onSetQuantity = { ticketId, qty ->
            appState.cartViewModel.updateQuantity(ticketId, qty)
        },
        onRemoveItem = { ticketId -> appState.cartViewModel.removeFromCart(ticketId) },
        onCheckout = {
                orgId,
                jurisdictionEligibilityConfirmed,
                onSuccess,
                onError ->

            appState.cartViewModel.checkout(
                orgId = orgId,
                jurisdictionEligibilityConfirmed =
                    jurisdictionEligibilityConfirmed,
                onResult = { result ->
                    when (result) {
                        is CheckoutResult.Success -> {
                            onSuccess()
                        }

                        CheckoutResult.Cancelled -> {
                            onError("Checkout cancelled")
                        }

                        is CheckoutResult.InventoryError -> {
                            onError(result.error.errorMessage)
                        }

                        is CheckoutResult.Failure -> {
                            onError(
                                result.exception.message
                                    ?: "Checkout failed"
                            )
                        }
                    }
                },
                onError = onError
            )
        },
        onConfirmCartInfoAlert = { updatedProfile ->
            appState.authViewModel.updateProfile(updatedProfile)
        },
        onBack = {
            appState.navController.navigate(Screen.EventsScreen.route) {
                popUpTo(0) {
                    inclusive = true
                }
                launchSingleTop = true
            }
        },
        onBrowseEvents = {
            appState.navController.navigate(Screen.EventsScreen.route) {
                popUpTo(0) {
                    inclusive = true
                }
                launchSingleTop = true
            }
        }
    )
}

@Composable
fun CartScreen(
    appState: AppState,
    profile: Profile,
    uiState: CartUiState,
    onUpdateQuantity: (ticketId: String, quantity: Int) -> Unit,
    onSetQuantity: (ticketId: String, quantity: Int) -> Unit,
    onRemoveItem: (ticketId: String) -> Unit,
    onCheckout: (orgId: String, jurisdictionEligibilityConfirmed: Boolean, onSuccess: () -> Unit, onError: (String) -> Unit) -> Unit,
    onConfirmCartInfoAlert: (Profile) -> Unit,
    onBack: () -> Unit,
    onBrowseEvents: () -> Unit
) {
    val cartItems = uiState.items
    val coroutineScope = rememberCoroutineScope()
    var isConnectingToStripe by remember { mutableStateOf(false) }

    var expandedOrgs by remember {
        mutableStateOf(cartItems.firstOrNull()?.ticket?.createdById?.let { setOf(it) }
            ?: emptySet())
    }

    var showMultiOrgDialog by remember {
        mutableStateOf(
            profile.showCartInfoDialog &&
                    cartItems.map { it.ticket.createdById }.distinct().size > 1
        )
    }

    BoxWithConstraints(
        modifier = Modifier
            .fillMaxSize()
    ) {
        val maxWidthDp = maxWidth
        val targetWidth = when {
            maxWidthDp < 600.dp -> maxWidthDp
            maxWidthDp < 900.dp -> 600.dp
            else -> 720.dp
        }

        val contentModifier = Modifier
            .widthIn(max = targetWidth)
            .align(Alignment.TopCenter)

        if (cartItems.isEmpty()) {
            EmptyCartContent(onBrowseEvents = onBrowseEvents)

        } else {
            Column(
                modifier = contentModifier.padding(horizontal = 16.dp)
            ) {
                Spacer(Modifier.height(16.dp))

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.Center
                ) {
                    Text(
                        text = "Cart",
                        style = MaterialTheme.typography.headlineMedium
                    )
                }

                Spacer(Modifier.height(16.dp))

                val groupedByOrg = cartItems.groupBy { it.ticket.createdById ?: "unknown" }

                LazyColumn(
                    modifier = Modifier.weight(1f)
                ) {
                    groupedByOrg.forEach { (orgId, orgItems) ->

                        val orgName = orgItems.firstOrNull()?.ticket?.createdByName
                            ?: "Unknown Organization"

                        item(key = orgId) {
                            CartOrganizationCard(
                                orgName = orgName,
                                items = orgItems,
                                fees = uiState.fees ?: return@item,
                                expanded = expandedOrgs.contains(orgId),
                                onExpandToggle = {
                                    expandedOrgs =
                                        if (expandedOrgs.contains(orgId))
                                            expandedOrgs - orgId
                                        else expandedOrgs + orgId
                                },
                                onRemoveItem = onRemoveItem,
                                onUpdateQuantity = onUpdateQuantity,
                                onSetQuantity = onSetQuantity,
                                onSetShipping = { ticketId, selected ->
                                    appState.cartViewModel.updateShipping(
                                        ticketId,
                                        selected
                                    )
                                },
                                onCheckoutOrg = { jurisdictionEligibilityConfirmed ->
                                    isConnectingToStripe = true
                                    coroutineScope.launch {
                                        try {
                                            onCheckout(
                                                orgId,
                                                jurisdictionEligibilityConfirmed,
                                                { },
                                                { }
                                            )
                                        } finally {
                                            delay(5000)
                                            isConnectingToStripe = false
                                        }
                                    }
                                }
                            )

                            Spacer(Modifier.height(16.dp))
                        }
                    }
                }
            }
        }

        // Floating Back Button
        FloatingActionButton(
            onClick = onBack,
            containerColor = MaterialTheme.colorScheme.surface.copy(alpha = 0.85f),
            modifier = Modifier
                .align(Alignment.TopStart)
                .padding(12.dp)
                .size(40.dp)
        ) {
            Icon(
                imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                contentDescription = "Back",
                tint = MaterialTheme.colorScheme.primary
            )
        }

        // Stripe overlay
        if (isConnectingToStripe) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(Color.Black.copy(alpha = 0.45f)),
                contentAlignment = Alignment.Center
            ) {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    CircularProgressIndicator(
                        color = MaterialTheme.colorScheme.primary,
                        strokeWidth = 4.dp,
                        modifier = Modifier.size(48.dp)
                    )
                    Spacer(Modifier.height(12.dp))
                    Text(
                        "Connecting to Stripe...",
                        style = MaterialTheme.typography.bodyMedium.copy(color = Color.White)
                    )
                }
            }
        }

        if (uiState.inventoryErrorMessage != null) {
            isConnectingToStripe = false
            InventoryAlertDialog(
                message = uiState.inventoryErrorMessage,
                onDismiss = { appState.cartViewModel.dismissInventoryError() }
            )
        }

        if (showMultiOrgDialog) {
            InfoAlertDialog(
                title = "Supporting Multiple Organizations!",
                message = "Thank you for spreading your generosity! You have items from more than one organization in your cart. Please complete checkout for each organization separately.",
                onDismiss = {
                    showMultiOrgDialog = false
                    onConfirmCartInfoAlert(
                        profile.copy(showCartInfoDialog = false)
                    )
                },
                confirmText = "OK"
            )
        }
    }
}